import React, { useState, useEffect, useMemo } from "react";
import "./App.css";
import CustomerInfo from "./components/CustomerInfo";
import ProductSpecs from "./components/ProductSpecs";
import InstrumentReading from "./components/InstrumentReading";
import WindingTemperatureGradientReading from "./components/WindingTemperatureGradientReading";
import SwitchOperation from "./components/SwitchOperation";
import HighVoltageTest from "./components/HighVoltageTest";
import InsulationResistanceTest from "./components/InsulationResistanceTest";
import TestCondition from "./components/TestCondition";
import TestResults from "./components/TestResults";
import ReportPreview from "./components/ReportPreview";

function App() {
  // Get today's date in YYYY-MM-DD format (required for HTML date input)
  const getTodayDate = () => {
    const today = new Date();
    const year = today.getFullYear();
    const month = String(today.getMonth() + 1).padStart(2, '0');
    const day = String(today.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const [currentStep, setCurrentStep] = useState(1);
  const todayDate = getTodayDate();
  
  const [formData, setFormData] = useState({
    // Customer Info
    customer: "",
    poNo: "",
    poDate: todayDate,
    invoiceNo: "",
    invoiceDate: todayDate,
    date: todayDate,
    instrumentSlNo: "",
    model: "",

    // Product Specifications
    modelNo: "4031",
    range: "0 - 150 °C",
    dialGraduation: "2 °C",
    accuracy: "1.5% of FSD",
    capillaryLength: "",
    particular: "",
    noOfSwitches: "2",
    switchType: "Micro Switch",
    switchRating: "5A @ 250VAC & 0.5A @ 250V DC",
    switchDifferential: "4 ± 2°C",
    degreeOfProtection: "",
    accessories: "",
    // WTI-specific fields
    gradient: "Standard (100%Loading)",
    currentInAmps: "5",
    windingTemperature: "15",
    requestedTemperatureRise: "2",

    // Instrument Reading
    standardReadings: {
      40: { forward: "40", reverse: "" },
      60: { forward: "60", reverse: "61" },
      80: { forward: "80", reverse: "" },
      100: { forward: "100", reverse: "101" },
      120: { forward: "120", reverse: "" },
      140: { forward: "141", reverse: "" },
    },
    // CCU Output values (only shown when Accessories = "External CCU")
    ccuOutput1: {
      40: "8.27",
      60: "10.41",
      80: "12.50",
      100: "16.67",
      120: "16.81",
      140: "18.96",
    },
    ccuOutput2: {
      40: "8.27",
      60: "10.41",
      80: "12.50",
      100: "16.67",
      120: "16.81",
      140: "18.96",
    },
    // CCU and RI values (only shown when Accessories = "External CCU and RI")
    remoteIndicatorCCU1: {
      40: "40",
      60: "59",
      80: "79",
      100: "99",
      120: "120",
      140: "141",
    },
    ccuOutput2ForRI: {
      40: "8.27",
      60: "10.41",
      80: "12.50",
      100: "16.67",
      120: "16.81",
      140: "18.96",
    },
    // CCU and RI (1 O/P) values (only shown when Accessories = "External CCU and RI (1 O/P)")
    remoteIndicatorCCU1For1OP: {
      40: "40",
      60: "59",
      80: "79",
      100: "99",
      120: "120",
      140: "141",
    },
    ccuOutput2ForRI1OP: {
      40: "8.27",
      60: "10.41",
      80: "12.50",
      100: "16.67",
      120: "16.81",
      140: "18.96",
    },
    remoteIndicatorOP1ForRI1OP: {
      40: "8.27",
      60: "10.41",
      80: "12.50",
      100: "16.67",
      120: "16.81",
      140: "18.96",
    },
    // CCU and RI (2 O/P) values (only shown when Accessories = "External CCU and RI (2 O/P)")
    remoteIndicatorCCU1For2OP: {
      40: "40",
      60: "59",
      80: "79",
      100: "99",
      120: "120",
      140: "141",
    },
    ccuOutput2ForRI2OP: {
      40: "8.27",
      60: "10.41",
      80: "12.50",
      100: "16.67",
      120: "16.81",
      140: "18.96",
    },
    remoteIndicatorOP1ForRI2OP: {
      40: "8.27",
      60: "10.41",
      80: "12.50",
      100: "16.67",
      120: "16.81",
      140: "18.96",
    },
    remoteIndicatorOP2ForRI2OP: {
      40: "8.27",
      60: "10.41",
      80: "12.50",
      100: "16.67",
      120: "16.81",
      140: "18.96",
    },
    // CCU and RI (4 O/P) values (only shown when Accessories = "External CCU and RI (4 O/P)")
    remoteIndicatorCCU1For4OP: {
      40: "40",
      60: "59",
      80: "79",
      100: "99",
      120: "120",
      140: "141",
    },
    ccuOutput2ForRI4OP: {
      40: "8.27",
      60: "10.41",
      80: "12.50",
      100: "16.67",
      120: "16.81",
      140: "18.96",
    },
    remoteIndicatorOP1ForRI4OP: {
      40: "8.27",
      60: "10.41",
      80: "12.50",
      100: "16.67",
      120: "16.81",
      140: "18.96",
    },
    remoteIndicatorOP2ForRI4OP: {
      40: "8.27",
      60: "10.41",
      80: "12.50",
      100: "16.67",
      120: "16.81",
      140: "18.96",
    },
    remoteIndicatorOP3ForRI4OP: {
      40: "8.27",
      60: "10.41",
      80: "12.50",
      100: "16.67",
      120: "16.81",
      140: "18.96",
    },
    remoteIndicatorOP4ForRI4OP: {
      40: "8.27",
      60: "10.41",
      80: "12.50",
      100: "16.67",
      120: "16.81",
      140: "18.96",
    },

    // Switch Operation
    s1: {
      setPoint: "",
      onAt: "",
      offAt: "",
      differential: "",
    },
    s2: {
      setPoint: "",
      onAt: "",
      offAt: "",
      differential: "",
    },

    // High Voltage Test
    hvTest: {
      equipment: "MICRODYNE",
      testVoltage: "2.5 KV AC, 50Hz",
      testDescription: "All terminals to Body for 1min.",
      result: "Pass",
    },
    // High Voltage Test for H models (4031H-OTI, 4031H-WTI)
    hvTestH: [
      {
        equipment: "MICRODYNE",
        testVoltage: "500V AC, 50Hz",
        dut: "OTI/WTI",
        testDescription: "Sensor (3 core cable) terminal to the earth",
        result: "pass",
      },
      {
        equipment: "MICRODYNE",
        testVoltage: "2.5 KV AC, 50Hz",
        dut: "OTI/WTI",
        testDescription: "All terminal to the earth for 1 min",
        result: "pass",
      },
      {
        equipment: "MICRODYNE",
        testVoltage: "500V AC, 50Hz",
        dut: "CCU/ Digital Indicator",
        testDescription: "Output & OTI/WTI input terminals to the earth for 1 min.",
        result: "pass",
      },
      {
        equipment: "MICRODYNE",
        testVoltage: "2KV AC, 50Hz",
        dut: "CCU/ Digital Indicator",
        testDescription: "Power supply terminals to the earth for 1min",
        result: "pass",
      },
    ],

    // Insulation Resistance Test
    irTest: {
      instrument: "OTI",
      testVoltage: "500 VDC",
      terminalDetails: "All terminals to earth",
      measuredValue: "Greater than 100M ohms",
      result: "Pass",
    },

    // Winding Temperature Gradient Reading (for 4031-WTI and 4031H-WTI models)
    windingTempGradient: {
      ctSecondaryCurrent: { initial: '0', g1: '5', g2: '', g3: '' },
      overload120Percent: { initial: '-', g1: '-', g2: '6', g3: '' },
      maxContinuousCurrent: { initial: '-', g1: '-', g2: '6', g3: '' },
      reqTempRise: { initial: '0', g1: '15', g2: '', g3: '' },
      wtiReading: { initial: '60', g1: '75', g2: '', g3: '' },
    },

    // Test Condition
    testCondition: [],

    // Test Results
    testResults: {
      indicationAccuracy: true,
      switchOperation: true,
      switchDifferential: true,
      visualFinish: true,
    },
    testResultsList: [],

    // Tested By
    testedBy: "",
    approvedBy: "",
  });

  // Check if model is WTI variant (4031-WTI or 4031H-WTI)
  const isWTIModel = useMemo(() => {
    return formData.model && (formData.model === '4031-WTI' || formData.model === '4031H-WTI');
  }, [formData.model]);

  // Base steps array
  const allSteps = [
    { id: 1, title: "Customer Information", component: CustomerInfo },
    { id: 2, title: "Product Specifications", component: ProductSpecs },
    { id: 3, title: "Instrument Reading", component: InstrumentReading },
    { id: 4, title: "Switch Operation", component: SwitchOperation },
    { id: 5, title: "Winding Temperature Gradient Reading", component: WindingTemperatureGradientReading, wtiOnly: true },
    { id: 6, title: "High Voltage Test", component: HighVoltageTest },
    {
      id: 7,
      title: "Insulation Resistance Test",
      component: InsulationResistanceTest,
    },
    { id: 8, title: "Test Condition", component: TestCondition },
    { id: 9, title: "Test Results", component: TestResults },
    { id: 10, title: "Report Preview", component: ReportPreview },
  ];

  // Filter steps based on model and renumber them
  const steps = useMemo(() => {
    return allSteps
      .filter(step => !step.wtiOnly || isWTIModel)
      .map((step, index) => ({ ...step, id: index + 1 }));
  }, [isWTIModel]);

  // Adjust current step if it becomes invalid when model changes
  useEffect(() => {
    if (currentStep > steps.length) {
      setCurrentStep(steps.length);
    }
  }, [steps.length, currentStep]);

  const updateFormData = (stepData) => {
    setFormData((prev) => ({ ...prev, ...stepData }));
  };

  const nextStep = () => {
    if (currentStep < steps.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const goToStep = (step) => {
    setCurrentStep(step);
  };

  // Get the current component based on the filtered steps
  const CurrentComponent = steps[currentStep - 1]?.component;

  return (
    <div className="App">
      <header className="App-header">
        <h1>Quality Assurance Test Report Generator</h1>
      </header>

      <div className="step-indicator">
        {steps.map((step, index) => (
          <div
            key={step.id}
            className={`step ${currentStep === step.id ? "active" : ""} ${
              currentStep > step.id ? "completed" : ""
            }`}
            onClick={() => goToStep(step.id)}
          >
            <div className="step-number">{step.id}</div>
            <div className="step-title">{step.title}</div>
          </div>
        ))}
      </div>

      <div className="form-container">
        <CurrentComponent
          formData={formData}
          updateFormData={updateFormData}
          nextStep={nextStep}
          prevStep={prevStep}
          currentStep={currentStep}
          totalSteps={steps.length}
        />
      </div>
    </div>
  );
}

export default App;
